import React, { useState, useEffect } from 'react';
import {
  LuSchool,
  LuFlame,
  LuWaves,
  LuAnchor,
  LuBookOpen,
  LuAward,
  LuUsers,
  LuHeart,
  LuSparkles
} from 'react-icons/lu';
import { GiBookmarklet, GiPalmTree, GiSailboat, GiChurch } from 'react-icons/gi';
import { PiChurchLight } from 'react-icons/pi';
import { FaCross } from 'react-icons/fa';
import parishesHero from '../../assets/hnh.png';
import diocesanLogo from '../../assets/image.png';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';

export default function AboutDiocese({ activeSection = 'about', onSelectSection = () => { } }) {
  const [bishopData, setBishopData] = useState({
    name: '',
    img_url: ''
  });

  useEffect(() => {
    let isMounted = true;
    async function fetchBishop() {
      try {
        const snap = await getDocs(collection(db, 'bishop'));
        if (!snap.empty) {
          const doc = snap.docs[0].data();
          if (isMounted) {
            setBishopData({
              name: doc.name || doc.bishop_name || doc.title || '',
              img_url: doc.img_url || doc.image || doc.img || ''
            });
          }
        }
      } catch (err) {
        console.error("Error fetching bishop data:", err);
      }
    }
    fetchBishop();
    return () => { isMounted = false; };
  }, []);

  const visionPillars = [
    {
      title: "Churches Renewed",
      desc: "Churches renewed through prayer, worship, unity and faithful service.",
      icon: GiChurch,
      color: "bg-[#842343]/10 text-[#842343]"
    },
    {
      title: "Families Strengthened",
      desc: "Families strengthened through Christian love, fellowship and spiritual formation.",
      icon: LuHeart,
      color: "bg-[#D4A64A]/15 text-[#9A701E]"
    },
    {
      title: "Youth Empowered",
      desc: "Youth empowered to become disciples, leaders and witnesses of Christ.",
      icon: LuSparkles,
      color: "bg-blue-50 text-blue-600"
    },
    {
      title: "Communities Transformed",
      desc: "Communities transformed through compassion, justice, peace and hope.",
      icon: LuUsers,
      color: "bg-emerald-50 text-emerald-600"
    },
    {
      title: "Gospel Proclaimed",
      desc: "The Gospel proclaimed through word and action across all walks of life.",
      icon: LuBookOpen,
      color: "bg-purple-50 text-purple-600"
    },
    {
      title: "Creation Cared For",
      desc: "Creation cared for through responsible ecological stewardship and sustainability.",
      icon: GiPalmTree,
      color: "bg-teal-50 text-teal-600"
    }
  ];

  const logoTheme = [
    {
      title: "The School & Chimney",
      desc: "Reminds us of the educational institutions and tile industries established by Basel Missionaries. They signify the Church's active engagement in society.",
      icon: LuSchool,
      color: "bg-blue-50 text-blue-600"
    },
    {
      title: "The Holy Bible",
      desc: "The spiritual foundation of the Diocese and its members. It represents the importance of daily Scripture reading, meditation, and lived Christian witness.",
      icon: GiBookmarklet,
      color: "bg-amber-50 text-amber-600"
    },
    {
      title: "The Sail-Borne Ship",
      desc: "Symbolizes the pioneering missionaries who sailed to the Malabar Coast to evangelize. It also represents our journey of faith guided by the Holy Spirit.",
      icon: GiSailboat,
      color: "bg-teal-50 text-teal-600"
    },
    {
      title: "The Coconut Tree",
      desc: "Signifies the Diocese's commitment to ecological stewardship and the beauty of Kerala. Its lush green fronds represent growth and vitality.",
      icon: GiPalmTree,
      color: "bg-emerald-50 text-emerald-600"
    },
    {
      title: "The Episcopal Stole",
      desc: "The sail shaped like an episcopal stole represents the heritage and historical episcopacy of the Church of South India.",
      icon: LuAnchor,
      color: "bg-indigo-50 text-indigo-600"
    },
    {
      title: "The Central Cross",
      desc: "A universal symbol of sacrificial love, redemption, and victory through Jesus Christ, encouraging us to abide in divine love.",
      icon: FaCross,
      color: "bg-rose-50 text-[#842343]"
    },
    {
      title: "The Mountain & The Sea",
      desc: "Depicts the diverse landscapes of Malabar, particularly Wayanad's hills and the coastal Arabian sea.",
      icon: LuWaves,
      color: "bg-cyan-50 text-cyan-600"
    },
    {
      title: "The Burning Candle",
      desc: "We are called to be like a candle burning brightly for Christ—providing light and warmth while selflessly sacrificing for the Kingdom.",
      icon: LuFlame,
      color: "bg-orange-50 text-orange-600"
    }
  ];

  const subPages = [
    { title: "History & Heritage", desc: "175+ years of missionary heritage", key: "history" },
    { title: "Bishop's Office", desc: "Pastoral message and episcopal vision", key: "bishop" },
    { title: "Administration & Council", desc: "Diocesan leadership, office bearers & synod", key: "admin" },
    { title: "Lights of Diocese", desc: "Spiritual workers and pastors", key: "lights" },
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2D2420] font-sans pb-16">

      {/* ── 1. Hero Header Banner ── */}
      <section className="relative w-full overflow-hidden min-h-[380px] sm:min-h-[420px] lg:min-h-[440px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={parishesHero}
            alt="About CSI Diocese of Malabar Cathedral"
            className="w-full h-full object-cover object-[right_center] filter brightness-[0.75] contrast-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#140e1b]/90 via-[#140e1b]/40 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-12 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          
          <div className="max-w-2xl text-left">
            <h1 className="font-serif font-bold text-white text-[38px] sm:text-[48px] lg:text-[56px] tracking-[0.04em] uppercase leading-[1.05] drop-shadow-md">
              ABOUT DIOCESE
            </h1>
            <p className="text-[#E8B86D] text-[15px] sm:text-[17px] font-medium tracking-wide mt-2.5 drop-shadow-sm">
              A vibrant communion of worship, witness, and Christian service under episcopal leadership, sharing the transformative love of Christ across Northern Kerala.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium mt-4">
              <span onClick={() => onSelectSection('home')} className="hover:text-white cursor-pointer transition-colors">Home</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span className="text-white">About Diocese</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuAward className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">175+</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Years Legacy</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <PiChurchLight className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">26+</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Diocese</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <span className="text-[#E8B86D] text-xl font-serif leading-none">✝</span>
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">4.5M+</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Members</span>
              </div>
            </div>
          </div>

        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-12 text-[#FAF7F2] fill-current">
            <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* ── 2. Quick Subpage Navigation Cards ── */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
          {subPages.map((sub, idx) => (
            <button
              key={idx}
              onClick={() => onSelectSection(sub.key)}
              className="bg-white rounded-2xl p-4 border border-gray-100 shadow-[0_4px_15px_rgba(0,0,0,0.02)] hover:shadow-[0_8px_25px_rgba(132,35,67,0.08)] hover:-translate-y-0.5 transition-all text-left group cursor-pointer"
            >
              <h3 className="font-serif font-bold text-[14px] text-[#1a1715] group-hover:text-[#842343] transition-colors mb-1 leading-snug">
                {sub.title}
              </h3>
              <p className="text-[10.5px] text-[#8A7E79] leading-tight line-clamp-2">
                {sub.desc}
              </p>
              <span className="text-[#842343] text-xs font-bold mt-2 block opacity-0 group-hover:opacity-100 transition-opacity">
                Explore →
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-12 space-y-12">

        {/* ── 3. Section 1: Diocesan Bishop Profile ── */}
        <div className="bg-white rounded-[32px] p-6 sm:p-10 border border-[#E8DDD7] shadow-[0_10px_35px_rgba(132,35,67,0.04)] grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left Bishop Portrait */}
          <div className="lg:col-span-5 flex flex-col items-center">
            <div className="w-full max-w-sm rounded-[28px] overflow-hidden shadow-lg border-[6px] border-white aspect-[4/5] bg-[#FAF6F2]">
              <img
                src={bishopData.img_url}
                alt={bishopData.name}
                className="w-full h-full object-cover object-top hover:scale-102 transition-transform duration-700"
              />
            </div>
            <div className="mt-4 text-center">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4A64A] block mb-1">
                EPISCOPAL LEADERSHIP
              </span>
              <h3 className="font-serif font-bold text-[#842343] text-xl sm:text-2xl">
                {bishopData.name}
              </h3>
              <span className="text-xs text-[#7B6B66] font-semibold block mt-0.5">
                Diocesan Bishop, CSI Diocese of Malabar
              </span>
            </div>
          </div>

          {/* Right Bishop Bio */}
          <div className="lg:col-span-7 space-y-4 text-left">
            <div className="inline-flex items-center gap-2 bg-[#FAF0F2] text-[#842343] px-3.5 py-1.5 rounded-full text-xs font-bold border border-[#F2D5DC]">
              <GiChurch className="w-4 h-4" />
              <span>Diocesan Bishop</span>
            </div>

            <h2 className="font-serif font-bold text-[#1a1715] text-2xl sm:text-3xl lg:text-[34px] leading-tight">
              Rt. Rev. Dr. Royce Manoj Victor
            </h2>

            <p className="text-[#4A3F3B] text-[15px] sm:text-[16px] leading-[1.85] font-sans">
              Rt. Rev. Dr. Royce Manoj Victor serves as the Bishop of the Church of South India Diocese of Malabar and was consecrated as the first Bishop of the Diocese on 17 May 2016. Under his episcopal leadership, the Diocese seeks to grow as a vibrant community of worship, witness and service.
            </p>

            <p className="text-[#4A3F3B] text-[15px] sm:text-[16px] leading-[1.85] font-sans">
              His ministry is characterised by a commitment to spiritual renewal, pastoral care, Christian formation, mission and compassionate engagement with society. He encourages the Church to remain firmly rooted in the Gospel while responding faithfully to the changing realities and needs of the world. His episcopal vision seeks to strengthen congregations, families and young people and to encourage the Church to be a visible witness to Christ through service, compassion, justice and hope.
            </p>

            <div className="pt-2 flex flex-wrap gap-4">
              <button
                onClick={() => onSelectSection('bishop')}
                className="bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-bold py-3 px-6 rounded-xl shadow-xs transition-colors cursor-pointer"
              >
                Visit Bishop's Office →
              </button>
            </div>
          </div>
        </div>

        {/* ── 4. Section 2: Theology: Rooted in Christ, Scripture and Mission ── */}
        <div className="bg-gradient-to-br from-[#FAF5EF] via-[#F8F0E7] to-[#F1E5D8] rounded-[32px] p-6 sm:p-10 lg:p-12 border border-[#E8DDD7] shadow-[0_10px_35px_rgba(0,0,0,0.03)] text-left">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/80 text-[#842343] px-3.5 py-1.5 rounded-full text-xs font-bold border border-[#E8DDD7] mb-3">
              <LuBookOpen className="w-4 h-4 text-[#D4A64A]" />
              <span>Our Theological Foundation</span>
            </div>

            <h2 className="font-serif font-bold text-[#842343] text-2xl sm:text-3xl lg:text-[34px] leading-tight mb-2">
              Theology
            </h2>
            <h3 className="font-serif font-bold text-[#2D2420] text-lg sm:text-xl mb-6">
              Rooted in Christ, Scripture and Mission
            </h3>

            <div className="space-y-5 text-[#4A3F3B] text-[15px] sm:text-[16px] leading-[1.85]">
              <p>
                The theological foundation of the Diocese of Malabar is centred on Jesus Christ, the Holy Scriptures and the mission of God. The Bible is regarded as the foundation of the life of the Diocese, calling every believer to read, study and meditate upon God's Word.
              </p>

              <p>
                We believe that Christian faith is not confined to worship within the Church but is expressed through a life of discipleship, love, compassion, justice and service. The Church is called to proclaim the Gospel, nurture people in faith, care for the vulnerable, promote peace and reconciliation, and participate in God's transforming work in the world.
              </p>

              <p>
                Our theology therefore brings together worship and witness, faith and action, spiritual formation and social responsibility. As a united Church within the Church of South India, we also value Christian unity and ecumenical fellowship as an essential expression of our witness to Christ.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 pt-6 border-t border-[#E8DDD7]">
              <div className="bg-white/80 p-4 rounded-2xl border border-white/60">
                <span className="text-xs font-bold uppercase tracking-wider text-[#842343] block mb-1">Christ-Centred</span>
                <p className="text-xs text-[#5F5A58]">Rooted in Jesus Christ and guided by the Holy Scriptures.</p>
              </div>
              <div className="bg-white/80 p-4 rounded-2xl border border-white/60">
                <span className="text-xs font-bold uppercase tracking-wider text-[#842343] block mb-1">Faith in Action</span>
                <p className="text-xs text-[#5F5A58]">Discipleship expressed through compassion, justice, and service.</p>
              </div>
              <div className="bg-white/80 p-4 rounded-2xl border border-white/60">
                <span className="text-xs font-bold uppercase tracking-wider text-[#842343] block mb-1">Christian Unity</span>
                <p className="text-xs text-[#5F5A58]">Committed to ecumenical fellowship and united witness in CSI.</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── 5. Section 3: Vision: Walking in the Gospel: A Journey of Renewal ── */}
        <div className="bg-gradient-to-br from-[#842343] via-[#7B1E3A] to-[#5A1428] rounded-[32px] p-6 sm:p-10 lg:p-12 text-white shadow-xl relative overflow-hidden text-left">
          <div className="relative z-10 max-w-5xl">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E8B86D] block mb-2">
              DIOCESAN VISION
            </span>

            <h2 className="font-serif font-bold text-[#E8B86D] text-3xl sm:text-4xl lg:text-[40px] leading-tight mb-2 drop-shadow-sm">
              Walking in the Gospel: A Journey of Renewal
            </h2>

            <p className="text-white/90 text-[15px] sm:text-[16px] leading-[1.8] mt-4 mb-4">
              The vision of the Diocese of Malabar is to be a Christ-centred, spiritually renewed and mission-oriented Church, proclaiming the Gospel and serving God and society with faithfulness and compassion.
            </p>

            <div className="bg-white/10 backdrop-blur-md p-5 rounded-2xl border border-white/15 my-6">
              <p className="text-[#E8B86D] font-serif font-bold text-lg mb-2">
                2026 Year of Renewal Theme: “Walking in the Gospel: A Journey of Renewal”
              </p>
              <p className="text-white/85 text-[14px] leading-relaxed">
                In 2026, the Diocese observes the Year of Renewal under the theme <em>“Walking in the Gospel: A Journey of Renewal.”</em> This vision calls the Church to awaken its faith, renew its commitment and rediscover its mission. Renewal begins with the transformation of the heart and mind through God's Word and Spirit and extends into the life of the Church and society.
              </p>
            </div>

            <h4 className="font-serif font-bold text-xl text-[#E8B86D] mb-4">
              Our vision is to see:
            </h4>

            {/* 6 Vision Pillars Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {visionPillars.map((pillar, idx) => {
                const IconComponent = pillar.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white/10 hover:bg-white/15 backdrop-blur-sm p-5 rounded-2xl border border-white/15 transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-3 text-white">
                      <IconComponent className="w-5 h-5 text-[#E8B86D]" />
                    </div>
                    <h5 className="font-serif font-bold text-[16px] text-white mb-1.5">
                      {pillar.title}
                    </h5>
                    <p className="text-xs text-white/80 leading-relaxed">
                      {pillar.desc}
                    </p>
                  </div>
                );
              })}
            </div>

            <p className="text-white/85 text-[14px] sm:text-[15px] leading-[1.8] border-t border-white/20 pt-6">
              We seek to be a Church where faith becomes visible in service, renewal becomes a way of life, and the light of Christ shines through every congregation and every member. This reflects the Diocese's wider commitment to Gospel proclamation, spiritual formation, care for the marginalized, education, healing, peace, justice, compassion and creation care.
            </p>
          </div>

          <div className="absolute -right-10 -bottom-10 opacity-10 pointer-events-none">
            <GiChurch className="w-96 h-96 text-white" />
          </div>
        </div>

        {/* ── 6. The Meaning of Diocesan Emblem ── */}
        <div className="bg-white rounded-[32px] p-6 sm:p-10 border border-[#E8DDD7] shadow-[0_6px_25px_rgba(0,0,0,0.03)]">
          <div className="text-center max-w-xl mx-auto mb-8">
            <span className="text-xs font-bold uppercase tracking-widest text-[#842343] block mb-1">Our Identity</span>
            <h2 className="font-serif font-bold text-[#1a1715] text-[26px] sm:text-[30px] leading-tight mb-2">
              The Diocesan Emblem & Symbols
            </h2>
            <p className="text-xs sm:text-sm text-[#8A7E79]">
              Each element in the CSI Diocese of Malabar insignia conveys deep spiritual and historical significance.
            </p>
          </div>

          {/* Official Church Logo & Explanation Showcase Card */}
          <div className="bg-gradient-to-br from-[#FAF5EF] via-[#FAF7F2] to-[#F5ECE5] rounded-2xl p-6 sm:p-8 border border-[#E8DDD7] mb-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Logo Display Column */}
            <div className="lg:col-span-4 flex flex-col items-center justify-center">
              <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-[0_10px_30px_rgba(132,35,67,0.08)] border border-[#E8DDD7] flex items-center justify-center group hover:scale-[1.03] transition-all duration-300">
                <img
                  src={diocesanLogo}
                  alt="CSI Diocese of Malabar Official Church Logo"
                  className="w-48 sm:w-56 h-auto object-contain drop-shadow-md"
                />
              </div>
              <div className="mt-4 text-center">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#842343] bg-[#842343]/10 px-3.5 py-1 rounded-full inline-block">
                  Official Church Crest
                </span>
                <p className="text-xs text-[#7B6B66] font-semibold mt-1.5">
                  Church of South India - Diocese of Malabar
                </p>
              </div>
            </div>

            {/* Logo Explanation Column */}
            <div className="lg:col-span-8 space-y-4 text-left">
              <div className="inline-flex items-center gap-2 bg-[#842343]/10 text-[#842343] px-3 py-1 rounded-full text-xs font-bold">
                <GiChurch className="w-4 h-4" />
                <span>Diocesan Crest & Emblem</span>
              </div>
              <h3 className="font-serif font-bold text-[#842343] text-xl sm:text-2xl">
                Meaning & Symbolism of the Diocesan Logo
              </h3>
              <p className="text-[#4A3F3B] text-[14px] sm:text-[15px] leading-[1.8]">
                The official emblem of the CSI Diocese of Malabar is a sacred crest that weaves together our rich historical legacy, regional identity in Northern Kerala, and spiritual mission. Inspired by over 175 years of missionary work originating with the Basel Evangelical Missionary Society in 1839, the crest highlights the holistic witness of the Church—uniting faith, education, social development, and creation care.
              </p>
              <div className="bg-white/80 p-4 rounded-xl border border-[#E8DDD7] flex items-start gap-3 shadow-2xs">
                <div className="w-9 h-9 rounded-lg bg-[#842343]/10 text-[#842343] flex items-center justify-center shrink-0 mt-0.5">
                  <FaCross className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-sm text-[#1a1715]">
                    Diocesan Motto: "That They All May Be One" (John 17:21)
                  </h4>
                  <p className="text-xs text-[#5A4F4A] leading-relaxed mt-0.5">
                    Reflecting Christ's prayer for unity, our logo serves as a constant reminder of ecumenical fellowship, Christian discipleship, and compassionate service across all parishes and institutions in Malabar.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {logoTheme.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="bg-[#FAF7F2]/70 hover:bg-[#FAF7F2] rounded-2xl p-5 border border-gray-100 transition-all hover:shadow-sm text-left"
                >
                  <div className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center mb-3 shadow-xs`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-serif font-bold text-[15px] text-[#1a1715] mb-1.5 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-[11.5px] text-[#5A4F4A] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* ── 7. Bottom Statistics Strip ── */}
      <section className="mt-14 pt-8 pb-8 bg-[#f5ede5]/70 border-t border-[#ede2d6]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 text-center">

            <div className="flex items-center justify-center gap-3">
              <PiChurchLight className="w-7 h-7 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">26+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Diocese</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuBookOpen className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">5</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Districts</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuSchool className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">120+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Institutions</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">49</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Presbyters</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">4.5M+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Members</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <span className="text-[#842343] text-xl font-serif">✝</span>
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">100+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Worship Services</span>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}